import { CopyOutlined, CopyTwoTone } from "@ant-design/icons";
import { Typography } from "antd";
import { conversionStateApi } from "api/TenantSlug/ConversionState/api";
import { useGetPayoutQuery, useUpdatePayoutMutation } from "api/TenantSlug/Payout/hooks";
import { LogDataSection } from "components/Drawers/Logs/shared/LogDataSection";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerSubCollapsible } from "components/UI/DrawerCollapsible/DrawerSubCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { PayoutResult } from "types/enums";

import { schema, TransactionReportEditFormData } from "./formSchema";

import PayoutData = App.Data.PayoutData;

interface TransactionReportEditFormProps {
    payoutData: PayoutData;
    formId: string;
    onSuccess?: () => void;
}

export const TransactionReportEditForm: FC<TransactionReportEditFormProps> = (props) => {
    const { t } = usePaldockTranslation("forms.transaction-report-edit");
    const [payoutData, setPayoutData] = useState(props.payoutData);

    const transactionQuery = useGetPayoutQuery(
        props.payoutData.id,
        {
            filter: {
                include:
                    "conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer,children,children.commission,currency,commission",
            },
        },
        { enabled: false }
    );

    // Separate mutation instances for advertiser and affiliate payouts
    const updateAdvertiserPayoutMutation = useUpdatePayoutMutation();
    const updateAffiliatePayoutMutation = useUpdatePayoutMutation();

    const handleSubmit = async (data: TransactionReportEditFormData) => {
        const mutations: Promise<unknown>[] = [];

        // Update advertiser payout
        mutations.push(
            updateAdvertiserPayoutMutation.mutateAsync({
                payoutId: payoutData.id,
                request: {
                    amount: data.amount ?? undefined,
                    result: data.result,
                },
            })
        );

        // Update affiliate payout if exists
        const affiliatePayout = payoutData.children?.[0];
        if (affiliatePayout) {
            mutations.push(
                updateAffiliatePayoutMutation.mutateAsync({
                    payoutId: affiliatePayout.id,
                    request: {
                        amount: data.affiliate_amount ?? undefined,
                    },
                })
            );
        }

        try {
            await Promise.all(mutations);
            const result = await transactionQuery.refetch();
            if (result.data) {
                setPayoutData(result.data.payout);
            }
            toast.success(t("messages.update_success"));
            props.onSuccess?.();
        } catch {
            // Errors are handled by react-query
        }
    };

    const defaultValues: TransactionReportEditFormData = {
        ...payoutData,
        affiliate_payout_id: payoutData.children?.[0]?.id ?? null,
        affiliate_amount: payoutData.children?.[0]?.amount ?? null,
    };

    return (
        <GeneralFormProvider<TransactionReportEditFormData>
            key={payoutData.id}
            onSubmit={handleSubmit}
            schema={schema}
            defaultValues={defaultValues}
            id={props.formId}
            resetOnSubmit={false}
        >
            <TransactionReportEditFormContent payoutData={payoutData} />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    payoutData: PayoutData;
}

const TransactionReportEditFormContent: FC<FormContentProps> = ({ payoutData }) => {
    const { t } = usePaldockTranslation("forms.transaction-report-edit");
    const { t: tG } = usePaldockTranslation("general");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tColumns } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tBase } = usePaldockTranslation();
    const generalFormUtils = useGeneralForm<TransactionReportEditFormData>();
    const { control } = generalFormUtils;

    // A payout fetched directly can itself be the affiliate (child) transaction — this happens when
    // no workspace payout exists for the conversion, so there is no parent row to nest it under.
    // Detect this by `user_id`, not by `parent_id`/`children`, since a lone affiliate payout has
    // `parent_id: null` and an empty `children` array, making it indistinguishable from a workspace-only payout.
    const isAffiliatePayout = !!payoutData.user_id;
    const affiliatePayout = isAffiliatePayout ? payoutData : payoutData.children?.[0];
    const workspaceCommission = isAffiliatePayout ? undefined : payoutData.commission;
    const affiliateCommission = isAffiliatePayout ? payoutData.commission : payoutData.children?.[0]?.commission;

    const conversionStatesPayload = useMemo(() => {
        if (!payoutData.origin_eid) return null;
        return {
            filter: {
                sort: "-created_at",
                filter: { origin_eid: payoutData.origin_eid, type: { in: ["lead", "click"] }, is_origin: true },
                include: "country,owner,offer,advertiser,formSubmissionValues",
            },
        };
    }, [payoutData.origin_eid]);

    const getConversionStatesQuery = useFetchAllPaginatedData(
        (req) => conversionStateApi.index(req),
        conversionStatesPayload || {},
        "conversions",
        { enabled: !!payoutData.origin_eid }
    );

    // Build affs/advs entries only for filled values
    const affsEntries = Array.from({ length: 10 }, (_, i) => {
        const key = `affs${i + 1}`;
        const value = payoutData.conversionState?.owner_data?.[key];
        return value ? { term: key, description: () => String(value) } : null;
    }).filter(Boolean);

    const advsEntries = Array.from({ length: 10 }, (_, i) => {
        const key = `advs${i + 1}`;
        const value = payoutData.conversionState?.advertiser_data?.[key];
        return value ? { term: key, description: () => String(value) } : null;
    }).filter(Boolean);

    const generalList = PropertiesList.create(payoutData, [
        {
            term: tFields("offer.label"),
            description: ({ conversionState }) => <EntityLinkCell.Offer entity={conversionState?.offer} />,
        },
        {
            term: tFields("advertiser.label"),
            description: ({ conversionState }) => <EntityLinkCell.Advertiser entity={conversionState?.advertiser} />,
        },
        {
            term: tFields("conversion_type.label"),
            description: ({ conversionState }) => conversionState?.type ?? <EmptyCell />,
        },
        {
            term: tFields("transaction_type.label"),
            description: (payout) => payout.type ?? <EmptyCell />,
        },
        {
            term: tFields("source.label"),
            description: ({ conversionState }) => conversionState?.source ?? <EmptyCell />,
        },
        {
            term: tFields("affiliate.label"),
            description: ({ conversionState }) => <EntityLinkCell.Owner entity={conversionState?.owner} />,
        },
        {
            term: tFields("channel.label"),
            description: ({ conversionState }) => <EntityLinkCell.Cascade entity={conversionState?.cascade} />,
        },
        {
            term: tColumns("conversion_id"),
            description: ({ conversionState }) => conversionState?.eid,
            render: ({ conversionState }) =>
                conversionState?.eid ? (
                    <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.eid} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("origin_id"),
            description: (payout) =>
                payout.origin_eid ? (
                    <ShortenedIdWithCopy charactersBetweenDots={35} id={payout.origin_eid} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("event_id"),
            description: (payout) =>
                payout.send_eid ? (
                    <ShortenedIdWithCopy charactersBetweenDots={35} id={payout.send_eid} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: t("fields.transaction_id.label"),
            description: (payout) => <ShortenedIdWithCopy charactersBetweenDots={35} id={payout.id} />,
        },
        {
            term: tColumns("commission_id"),
            description: () => workspaceCommission?.id,
            render: () =>
                workspaceCommission ? (
                    <span className="flex items-center gap-1">
                        <EntityLinkCell.Commission entity={workspaceCommission} />
                        <Typography.Text
                            copyable={{
                                text: workspaceCommission.id,
                                icon: [<CopyOutlined key="copy" />, <CopyTwoTone key="copied" />],
                                tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                            }}
                        />
                    </span>
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("affiliate_commission_id"),
            description: () => affiliateCommission?.id,
            render: () =>
                affiliateCommission ? (
                    <span className="flex items-center gap-1">
                        <EntityLinkCell.Commission entity={affiliateCommission} />
                        <Typography.Text
                            copyable={{
                                text: affiliateCommission.id,
                                icon: [<CopyOutlined key="copy" />, <CopyTwoTone key="copied" />],
                                tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                            }}
                        />
                    </span>
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: t("fields.external_id.label"),
            description: ({ conversionState }) =>
                conversionState?.advertiser_custom_id ? (
                    <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.advertiser_custom_id} />
                ) : (
                    <EmptyCell />
                ),
        },
        {
            term: tColumns("affiliate_cid"),
            description: ({ conversionState }) =>
                conversionState?.owner_custom_id ? (
                    <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionState.owner_custom_id} />
                ) : (
                    <EmptyCell />
                ),
        },
        ...affsEntries,
        ...advsEntries,
    ] as Parameters<typeof PropertiesList.create<PayoutData>>[1]);

    const dataList = PropertiesList.create(payoutData, [
        {
            term: tFields("created_at.label"),
            description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
        },
        {
            term: t("fields.origin_date.label"),
            description: ({ conversionState }) =>
                conversionState?.created_at ? <DateTimeCell value={conversionState.created_at} /> : <EmptyCell />,
        },
        ...(payoutData.result === PayoutResult.Approved
            ? [
                  {
                      term: t("fields.approved_at.label"),
                      description: () =>
                          payoutData.approved_at ? <DateTimeCell value={payoutData.approved_at} /> : <EmptyCell />,
                  },
              ]
            : []),
        {
            term: t("fields.affiliate_payout.label"),
            render: isAffiliatePayout ? (
                <InteractiveCell.Money
                    formControl={{ control, name: "amount" }}
                    currencyCode={payoutData.currency?.currency_code}
                />
            ) : affiliatePayout ? (
                <InteractiveCell.Money
                    formControl={{ control, name: "affiliate_amount" }}
                    currencyCode={affiliatePayout.currency?.currency_code}
                />
            ) : (
                <EmptyCell />
            ),
        },
        {
            term: t("fields.workspace_payout.label"),
            render: isAffiliatePayout ? (
                <EmptyCell />
            ) : (
                <InteractiveCell.Money
                    formControl={{ control, name: "amount" }}
                    currencyCode={payoutData.currency?.currency_code}
                />
            ),
        },
        {
            term: t("fields.result.label"),
            render: (
                <InteractiveCell.Select
                    options={[
                        {
                            value: PayoutResult.Approved,
                            label: tG("payout_result.approved"),
                        },
                        {
                            value: PayoutResult.Rejected,
                            label: tG("payout_result.rejected"),
                        },
                        {
                            value: PayoutResult.Pending,
                            label: tG("payout_result.pending"),
                        },
                    ]}
                    formControl={{ control, name: "result" }}
                />
            ),
        },
    ]);

    return (
        <DrawerContent className="flex flex-col gap-4 p-4">
            <DrawerCollapsible title={t("sections.general")}>
                <PropertiesList list={generalList} />
            </DrawerCollapsible>
            <DrawerCollapsible title={t("sections.data")}>
                <PropertiesList list={dataList} />
            </DrawerCollapsible>
            <DrawerCollapsible title={t("sections.lead_detail")} isDefaultOpen={false}>
                {getConversionStatesQuery.data?.map((conversion) => (
                    <DrawerSubCollapsible key={conversion.id} title={conversion.type}>
                        <LogDataSection
                            formSubmissionValues={conversion.formSubmissionValues}
                            noDataMessage={tBase("components.log-conversion-detail.messages.no_conversion_data")}
                        />
                    </DrawerSubCollapsible>
                ))}
            </DrawerCollapsible>
        </DrawerContent>
    );
};
